import { Container } from "react-bootstrap";
import { Tab, TabPanel, Tabs, TabsList } from "@merge-api/merge-javascript-shared";
import {
  useLoadAlertCounts,
  useLoadPaginatedAlerts,
  useLoadSearchMetadata,
} from "../hooks/useLoadPaginatedAlerts";
import AlertDashboardBadge from "./components/AlertDashboardBadge";
import { snakeCaseToFirstLetterUpperCase } from "../../../utils";
import {
  ALERT_CATEGORIES,
  AlertDashboardColumn,
  AlertSortDirection,
  AlertsData,
  SEARCH_METADATA_CATEGORIES,
  SearchMetadata,
} from "./components/utils/constants";
import { useState } from "react";
import { TypeaheadComparatorClusterOption } from "@merge-api/merge-javascript-shared/dist/designSystem/molecules/Typeahead/types/types";
import AlertDashboardTable from "./components/AlertDashboardTable";
import AlertDashboardHeader from "./components/AlertDashboardHeader";
import {
  constructAlertQueryParamsFromSearchFilters,
  getOrderByParam,
} from "./components/utils/helpers";
import { useHistory, useParams } from "react-router-dom";
import { navigateToAlertDashboardTab } from "../../../router/RouterUtils";
type RouteParams = {
  category: string | undefined;
  alertID: string | undefined;
};

const AlertDashboard = () => {
  const alertCategories = Object.values(ALERT_CATEGORIES);
  const history = useHistory();

  // Route params
  const { category, alertID } = useParams<RouteParams>();

  const normalizedCategory = category?.toUpperCase();
  const validCategory = alertCategories.find((c) => c === normalizedCategory);
  const tabIndex = validCategory ? alertCategories.indexOf(validCategory) : 0;
  const activeCategory = validCategory ?? ALERT_CATEGORIES.INTEGRATION;

  // Search state
  const [searchFilters, setSearchFilters] = useState<TypeaheadComparatorClusterOption[]>([]);

  // Sort state
  const [sortField, setSortField] = useState<AlertDashboardColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<AlertSortDirection>(undefined);

  // Persist alertID only on the first load (used for linking specific alerts.)
  const [initialAlertID] = useState(alertID);

  // Show resolved and silenced alerts by default if an alert ID is provided
  const [showResolved, setShowResolved] = useState(!!alertID);
  const [showSilenced, setShowSilenced] = useState(!!alertID);

  const orderBy = sortField
    ? getOrderByParam(sortField as AlertDashboardColumn, sortDirection)
    : undefined;

  // Hooks for loading paginated alerts for each category and alert counts
  const queryParams = {
    ...constructAlertQueryParamsFromSearchFilters(
      searchFilters,
      showResolved,
      showSilenced,
      orderBy
    ),
  };

  const handleSort = (field: AlertDashboardColumn | null) => {
    if (sortField === field) {
      setSortDirection((prev) => {
        if (prev === undefined) return "asc";
        if (prev === "asc") return "desc";
        if (prev === "desc") {
          setSortField(null);
          return undefined;
        }
        return "asc";
      });
      if (sortDirection === "desc") {
        setSortField(null);
      }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const alertsData: Record<ALERT_CATEGORIES, AlertsData> = {
    [ALERT_CATEGORIES.INTEGRATION]: useLoadPaginatedAlerts({
      alertCategory: ALERT_CATEGORIES.INTEGRATION,
      queryParams,
      alertID: initialAlertID, // Use the stored initial value
    }),
    [ALERT_CATEGORIES.ORGANIZATION]: useLoadPaginatedAlerts({
      alertCategory: ALERT_CATEGORIES.ORGANIZATION,
      queryParams,
      alertID: initialAlertID,
    }),
    [ALERT_CATEGORIES.LINKED_ACCOUNT]: useLoadPaginatedAlerts({
      alertCategory: ALERT_CATEGORIES.LINKED_ACCOUNT,
      queryParams,
      alertID: initialAlertID,
    }),
  };

  const [alertCounts, setAlertCounts] = useState<Record<ALERT_CATEGORIES, number>>(
    Object.fromEntries(Object.values(ALERT_CATEGORIES).map((category) => [category, 0])) as Record<
      ALERT_CATEGORIES,
      number
    >
  );
  const [searchMetadata, setSearchMetadata] = useState<
    Record<SEARCH_METADATA_CATEGORIES, SearchMetadata[]>
  >(() => {
    const initialState: Record<SEARCH_METADATA_CATEGORIES, SearchMetadata[]> = {
      [SEARCH_METADATA_CATEGORIES.INTEGRATION]: [],
      [SEARCH_METADATA_CATEGORIES.ORGANIZATION]: [],
      [SEARCH_METADATA_CATEGORIES.LINKED_ACCOUNT]: [],
      [SEARCH_METADATA_CATEGORIES.BLUEPRINT]: [],
    };
    return initialState;
  });

  useLoadAlertCounts(setAlertCounts, showResolved, showSilenced);
  useLoadSearchMetadata(setSearchMetadata);

  const onTabChange = (_: any, newValue: string | number | null) => {
    if (typeof newValue === "string" || typeof newValue === "number") {
      const index = Number(newValue);
      if (!isNaN(index)) {
        navigateToAlertDashboardTab(history, alertCategories[index]);
      }
    }
  };

  return (
    <Container fluid className="pb-8 px-8 pt-16">
      {/* Header */}
      <h2 className="mt-10">Alerts</h2>
      <br />
      <Tabs variant="button" onChange={onTabChange} value={tabIndex}>
        <TabsList>
          {alertCategories.map((category) => (
            <Tab key={category}>
              {snakeCaseToFirstLetterUpperCase(category)}{" "}
              <AlertDashboardBadge count={alertCounts[category]} />
            </Tab>
          ))}
        </TabsList>
        <AlertDashboardHeader
          category={activeCategory}
          searchFilters={searchFilters}
          setSearchFilters={setSearchFilters}
          showResolved={showResolved}
          setShowResolved={setShowResolved}
          showSilenced={showSilenced}
          setShowSilenced={setShowSilenced}
          searchMetadata={searchMetadata}
        />

        {/* Dynamic Tab Content */}
        {alertCategories.map((category) => (
          <TabPanel key={category}>
            <AlertDashboardTable
              alerts={alertsData[category].alerts}
              tableType={category}
              onNext={alertsData[category].onNext}
              hasNext={alertsData[category].hasNext}
              isLoading={alertsData[category].isLoading}
              usingSearchFilters={searchFilters.length > 0}
              activeAlertID={initialAlertID}
              sortField={sortField}
              sortDirection={sortDirection}
              handleSort={handleSort}
            />
          </TabPanel>
        ))}
      </Tabs>
    </Container>
  );
};

export default AlertDashboard;
